import { Box, Divider, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { ReportType } from "../../../../../../../../api/types/svTypes";
import { selectDataSetSettings } from "../../../../store/selectors/clientSettingsSelectors";
import { selectReportType } from "../../../../store/selectors/fieldsStateSelectors";
import { selectEditDisabled } from "../../../../store/selectors/uiSettingsSelectors";
import { chartRelatedReportTypes } from "../../../../utilities/chartRelatedReport.types";
import ChartOfAccountsSection from "../../financialStatement/chartOfAccounts/ChartOfAccountsSection.tsx";
import { lineSelectionKeeperProps } from "../../financialStatement/utils/lineSelection.ts";
import { FieldTreeScrollContext } from "./FieldTreeScrollContext.ts";
import type { FieldsSectionControl, FieldsSectionId } from "./fieldsExpansionState.ts";
import {
  areAllSectionsOpen,
  initialFieldsExpansionState,
  toggleAllSectionsAction,
  toggleSectionAction,
} from "./fieldsExpansionState.ts";
import DataSetSelector from "./DataSetSelector";
import FieldsTitle from "./FieldsTitle.tsx";
import ListLedgerSelector from "./ListLedgerSelector";
import DimensionsList from "./dimensions/DimensionsList";
import MeasuresWrapper from "./measures/Measures";
import MetricsWrapper from "./metrics/Metrics";

const metricsReportTypes = [ReportType.Pivot, ReportType.Tabular, ...chartRelatedReportTypes, ReportType.Tile];

export const FieldsComponent = () => {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const scrollContext = useMemo(() => ({ scrollElement, contentElement }), [scrollElement, contentElement]);
  const [searchText, setSearchText] = useState("");
  const [expansion, setExpansion] = useState(initialFieldsExpansionState);
  const dataSets = useSelector(selectDataSetSettings);
  const editDisabled = useSelector(selectEditDisabled);
  const reportType = useSelector(selectReportType);
  const isMultiDataSet = dataSets.length > 1;
  const isFinancialStatement = reportType === ReportType.FinancialStatement;
  const dimensionsOnly = reportType === ReportType.List;
  const showMetrics = !reportType || metricsReportTypes.includes(reportType);

  const getSection = (id: FieldsSectionId): FieldsSectionControl => ({
    ...expansion[id],
    onToggle: () => setExpansion(toggleSectionAction(id)),
  });
  const handleToggleAll = () => setExpansion(toggleAllSectionsAction);

  return (
    <Stack spacing={2} sx={{ height: "100%" }} {...(isFinancialStatement ? lineSelectionKeeperProps : undefined)}>
      {isMultiDataSet && !isFinancialStatement && (
        <Box sx={{ px: "1.3rem" }}>
          <DataSetSelector disabled={editDisabled} />
        </Box>
      )}
      <Box sx={{ px: "1.3rem" }}>
        <SearchField onSearch={(text) => setSearchText(text?.toLowerCase())} debounceTimeMs={500} fullWidth />
      </Box>
      <Divider />
      <ScrollableFlexContainer containerRef={setScrollElement} scrollContainerSx={{ px: "1.3rem" }}>
        <FieldTreeScrollContext value={scrollContext}>
          <Stack ref={setContentElement} spacing={1}>
            {isFinancialStatement && (
              <>
                <FieldsTitle allExpanded={areAllSectionsOpen(expansion)} onToggleAll={handleToggleAll} />
                <ChartOfAccountsSection
                  searchText={searchText}
                  disabled={editDisabled}
                  section={getSection("chartOfAccounts")}
                />
              </>
            )}
            {dimensionsOnly && <ListLedgerSelector />}
            <DimensionsList
              searchText={searchText}
              disabled={editDisabled}
              section={isFinancialStatement ? getSection("dimensions") : undefined}
            />
            {!dimensionsOnly && (
              <MeasuresWrapper
                searchText={searchText}
                disabled={editDisabled}
                section={isFinancialStatement ? getSection("measures") : undefined}
              />
            )}
            {showMetrics && <MetricsWrapper searchText={searchText} disabled={editDisabled} />}
          </Stack>
        </FieldTreeScrollContext>
      </ScrollableFlexContainer>
    </Stack>
  );
};

export default FieldsComponent;
